import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  useMunicipalityMetadataGrid,
  useMunicipalityMetadataCanEdit,
  useUpsertElectoralMetadataValuesBatch,
  useElectoralMetadataFieldsAccess
} from "../api/electoral-metadata/hooks";
import type { MunicipalityGridFieldDefinitionDto } from "../api/electoral-metadata/types";
import { useCounties, useMunicipalities } from "../api/map";
import type { MunicipalityFeatureProperties } from "../api/map/types";
import { useCurrentUserTeamContext } from "../api/users";
import { ImportMetadataDialog } from "./ImportMetadataDialog";
import kvantabExportScript from "./kvantab-export-script.js?raw";
import "./metadata-grid-page.css";

function getServerCellValue(
  values: { electoralDistrictId: string; fieldDefinitionId: string; value: string | null }[],
  districtId: string,
  fieldId: string
): string {
  const cell = values.find(
    (v) => v.electoralDistrictId === districtId && v.fieldDefinitionId === fieldId
  );
  return cell?.value ?? "";
}

export const MetadataGridPage = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const [selectedCountyId, setSelectedCountyId] = useState<string | null>(null);
  const [municipalityId, setMunicipalityId] = useState<string>(() => searchParams.get("municipalityId") ?? "");
  const [importOpen, setImportOpen] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, string>>({});

  useEffect(() => {
    const fromUrl = searchParams.get("municipalityId");
    if (fromUrl) setMunicipalityId(fromUrl);
  }, [searchParams]);

  const { data: teamContext } = useCurrentUserTeamContext();
  const hasPreselectedRef = useRef(false);
  useEffect(() => {
    if (hasPreselectedRef.current) return;
    if (!teamContext?.countyId || !teamContext?.municipalityPolygonId) return;
    if (selectedCountyId || municipalityId) return;
    setSelectedCountyId(teamContext.countyId);
    setMunicipalityId(teamContext.municipalityPolygonId);
    hasPreselectedRef.current = true;
  }, [teamContext?.countyId, teamContext?.municipalityPolygonId, selectedCountyId, municipalityId]);

  const countiesQuery = useCounties({ enabled: true });
  const municipalitiesQuery = useMunicipalities(
    { limit: 2000, countyId: selectedCountyId ?? undefined, includeGeometry: false },
    null,
    { enabled: true }
  );
  const municipalitiesResponse = municipalitiesQuery.data;

  const { data: grid, isLoading: gridLoading } = useMunicipalityMetadataGrid(
    municipalityId || null
  );
  const { data: canEditMetadata = false } = useMunicipalityMetadataCanEdit(municipalityId || null);
  const batchMutation = useUpsertElectoralMetadataValuesBatch(municipalityId || null);
  const { data: fieldsAccess } = useElectoralMetadataFieldsAccess();

  const handleCountyChange = useCallback((countyId: string | null) => {
    setSelectedCountyId(countyId);
    setMunicipalityId("");
  }, []);

  useEffect(() => {
    setPendingChanges({});
  }, [municipalityId]);

  const countyOptions = useMemo(() => {
    const data = countiesQuery.data;
    if (!data?.length) return [];
    return data.map((c) => ({ id: c.id, code: c.code, name: c.name }));
  }, [countiesQuery.data]);

  const municipalityOptions = useMemo(() => {
    const features = (municipalitiesResponse?.features ?? []) as Array<{
      type: string;
      properties?: MunicipalityFeatureProperties;
    }>;
    return features
      .filter((f) => f.properties)
      .map((f) => ({
        id: f.properties!.municipalityPolygonId,
        name: f.properties!.name,
        code: f.properties!.code
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [municipalitiesResponse]);

  const selectedCountyCode = useMemo(
    () => countyOptions.find((c) => c.id === selectedCountyId)?.code ?? "",
    [countyOptions, selectedCountyId]
  );
  const selectedMunicipalityCode = useMemo(
    () => municipalityOptions.find((m) => m.id === municipalityId)?.code ?? "",
    [municipalityOptions, municipalityId]
  );
  const kvantabUrl =
    selectedCountyCode && selectedMunicipalityCode
      ? `https://centerpartiet.kvantab.se/map/detail?lan=${selectedCountyCode}&kommun=${selectedMunicipalityCode}&map=val_idag_c`
      : null;

  const sortedFields = useMemo(
    () => grid?.fieldDefinitions ?? [],
    [grid?.fieldDefinitions]
  );

  const getCellValue = (
    districtId: string,
    fieldId: string
  ): string => {
    const key = `${districtId}|${fieldId}`;
    if (pendingChanges[key] !== undefined) return pendingChanges[key];
    return grid ? getServerCellValue(grid.values, districtId, fieldId) : "";
  };

  const handleCellChange = (
    districtId: string,
    fieldId: string,
    value: string
  ) => {
    if (!canEditMetadata) return;
    const key = `${districtId}|${fieldId}`;
    const serverValue = grid ? getServerCellValue(grid.values, districtId, fieldId) : "";
    const trimmed = value.trim();
    setPendingChanges((prev) => {
      if (trimmed === serverValue) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: trimmed };
    });
  };

  const pendingCount = Object.keys(pendingChanges).length;
  const handleSave = () => {
    if (pendingCount === 0) return;
    const values = Object.entries(pendingChanges).map(([key, value]) => {
      const [electoralDistrictId, fieldDefinitionId] = key.split("|");
      return {
        electoralDistrictId,
        fieldDefinitionId,
        value: value || null
      };
    });
    batchMutation.mutate(
      { values },
      {
        onSuccess: () => setPendingChanges({})
      }
    );
  };

  return (
    <section
      className="metadata-grid-page"
      aria-label="Valdistriktsmetadata per kommun"
    >
      <div className="metadata-grid-page__header">
        <h2>Valdistriktsmetadata</h2>
        <p className="metadata-grid-page__description">
          Välj län och kommun för att redigera metadata per valdistrikt. Kolumnerna
          motsvarar metadatafälten
          {fieldsAccess === true ? (
            <>{" "}
              <Link to="/admin/metadata-fields">Hantera fältdefinitioner</Link>.
            </>
          ) : (
            "."
          )}
        </p>

        <div className="metadata-grid-page__toolbar">
          <label htmlFor="metadata-grid-county">
            Län
          </label>
          {countiesQuery.isLoading && (
            <span className="metadata-grid-page__loading" aria-live="polite">
              Laddar län…
            </span>
          )}
          {!countiesQuery.isLoading && (
            <select
              id="metadata-grid-county"
              aria-label="Välj län"
              value={selectedCountyId ?? ""}
              onChange={(e) => handleCountyChange(e.target.value || null)}
            >
              <option value="">Välj län</option>
              {countyOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
          <label htmlFor="metadata-grid-municipality">
            Kommun
          </label>
          <select
            id="metadata-grid-municipality"
            aria-label="Välj kommun"
            value={municipalityId}
            onChange={(e) => setMunicipalityId(e.target.value)}
            disabled={municipalitiesQuery.isLoading}
          >
            <option value="">Välj kommun</option>
            {municipalityOptions.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} ({m.code})
              </option>
            ))}
          </select>
          <button
            type="button"
            className="button button--secondary"
            onClick={() => setImportOpen(true)}
            disabled={!municipalityId || !canEditMetadata}
            aria-label="Importera CSV"
            title={!canEditMetadata ? "Endast visning – du kan inte redigera metadata för denna kommun." : undefined}
          >
            Importera CSV
          </button>
          {pendingCount > 0 && canEditMetadata && (
            <button
              type="button"
              className="button button--primary"
              onClick={handleSave}
              disabled={batchMutation.isPending}
              aria-label="Spara ändringar"
            >
              {batchMutation.isPending ? "Sparar…" : `Spara (${pendingCount})`}
            </button>
          )}
        </div>
      </div>

      <details className="metadata-grid-page__kvantab">
        <summary className="metadata-grid-page__kvantab-summary">Hämta metadata från Kvantab</summary>
        <div className="metadata-grid-page__kvantab-inner">
          <p className="metadata-grid-page__kvantab-desc">
            Du kan hämta metadata från Centerpartiets Kvantab-karta. Öppna kartan via länken nedan (län och kommun är redan valda ovan):
          </p>
          {kvantabUrl ? (
            <p className="metadata-grid-page__kvantab-url">
              <a
                href={kvantabUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {kvantabUrl}
              </a>
            </p>
          ) : (
            <p className="metadata-grid-page__kvantab-url metadata-grid-page__kvantab-url--muted">
              Välj län och kommun ovan för att få en direktlänk till Kvantab-kartan.
            </p>
          )}
          <p className="metadata-grid-page__kvantab-usage-title">Användning:</p>
          <ol className="metadata-grid-page__kvantab-steps">
            <li>Öppna kartsidan i webbläsaren (länken ovan)</li>
            <li>Öppna Developer Console (F12)</li>
            <li>Klicka på &quot;Kopiera&quot;-knappen nedan och klistra in koden i konsolen</li>
            <li>Ange antal valdistrikt ni har{grid?.districts != null ? ` – denna kommun har ${grid.districts.length} valdistrikt` : ""}</li>
            <li>Hovra över kartområden så registreras tooltips; klicka &quot;Exportera CSV&quot; i overlay när du är klar</li>
          </ol>
          <div className="metadata-grid-page__kvantab-code-wrap">
            <pre className="metadata-grid-page__kvantab-code">
              <code>
                {kvantabExportScript.replace(
                  /__DEFAULT_COUNT__/g,
                  String(grid?.districts?.length ?? 64)
                )}
              </code>
            </pre>
            <button
              type="button"
              className="button button--secondary metadata-grid-page__kvantab-copy"
              onClick={() => {
                const defaultCount = grid?.districts?.length ?? 64;
                const script = kvantabExportScript.replace(
                  /__DEFAULT_COUNT__/g,
                  String(defaultCount)
                );
                void navigator.clipboard.writeText(script);
              }}
              aria-label="Kopiera skript till urklipp"
            >
              Kopiera
            </button>
          </div>
        </div>
      </details>

      {!municipalityId && (
        <p className="metadata-grid-page__hint">
          Välj en kommun ovan för att visa och redigera metadata.
        </p>
      )}

      {municipalityId && grid && !canEditMetadata && (
        <p className="metadata-grid-page__hint metadata-grid-page__hint--info">
          Endast visning – du kan inte redigera metadata för denna kommun.
        </p>
      )}

      {municipalityId && gridLoading && (
        <p>Laddar...</p>
      )}

      {municipalityId && !gridLoading && grid && (
        <div className="metadata-grid-page__table-wrap">
          <table className="metadata-grid-table">
            <thead>
              <tr>
                <th scope="col">Valdistrikt</th>
                {sortedFields.map((f) => (
                  <th key={f.id} scope="col">
                    {f.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.districts.map((district) => {
                const hasMissingMetadata = sortedFields.every(
                  (f) => !getCellValue(district.id, f.id)
                );
                return (
                <tr
                  key={district.id}
                  className={hasMissingMetadata ? "metadata-grid-table__row--missing" : undefined}
                >
                  <th scope="row" className="metadata-grid-table__district-cell">
                    <span className="metadata-grid-table__district-name">{district.name}</span>
                    <span
                      className="metadata-grid-table__district-tooltip"
                      role="tooltip"
                    >
                      {sortedFields.map((field) => {
                        const val = getCellValue(district.id, field.id);
                        if (val === "") return null;
                        return (
                          <span key={field.id} className="metadata-grid-table__district-tooltip-row">
                            <span className="metadata-grid-table__district-tooltip-label">{field.name}:</span>{" "}
                            {val}
                          </span>
                        );
                      })}
                      {sortedFields.every((f) => !getCellValue(district.id, f.id)) && (
                        <span className="metadata-grid-table__district-tooltip-empty">Inga siffror ännu</span>
                      )}
                    </span>
                  </th>
                  {sortedFields.map((field) => (
                    <td key={field.id}>
                      <MetadataCell
                        districtId={district.id}
                        field={field}
                        value={getCellValue(district.id, field.id)}
                        onBlur={(value) =>
                          handleCellChange(district.id, field.id, value)
                        }
                        disabled={batchMutation.isPending || !canEditMetadata}
                      />
                    </td>
                  ))}
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>
      )}

      {municipalityId && !gridLoading && grid && grid.districts.length === 0 && (
        <p className="metadata-grid-page__empty">
          Inga valdistrikt hittades för denna kommun.
        </p>
      )}

      <ImportMetadataDialog
        open={importOpen}
        municipalityId={municipalityId}
        onClose={() => setImportOpen(false)}
      />
    </section>
  );
};

interface MetadataCellProps {
  districtId: string;
  field: MunicipalityGridFieldDefinitionDto;
  value: string;
  onBlur: (value: string) => void;
  disabled?: boolean;
}

function MetadataCell({
  field,
  value,
  onBlur,
  disabled
}: MetadataCellProps): JSX.Element {
  const [local, setLocal] = useState(value);
  useEffect(() => {
    setLocal(value);
  }, [value]);

  const handleBlur = () => {
    const trimmed = local.trim();
    if (trimmed !== value) onBlur(trimmed);
  };

  if (field.type === "Decimal") {
    return (
      <input
        type="number"
        step="any"
        value={local}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={handleBlur}
        disabled={disabled}
        aria-label={`${field.name}`}
        className="metadata-grid-cell metadata-grid-cell--number"
      />
    );
  }
  return (
    <input
      type="text"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={handleBlur}
      disabled={disabled}
      aria-label={`${field.name}`}
      className="metadata-grid-cell"
    />
  );
}
